import { useState, useEffect } from 'react';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';
import { GroupSession, CreateGroupSessionRequest, UpdateGroupSessionRequest } from '@/types/groupSession';
import { Participant } from '@/types/category';
import { useToast } from '@/hooks/use-toast';
import { validateParticipantsForBackend, prepareParticipantsForBackend, parseBackendValidationError } from '@/utils/participantValidation';

export const useGroupSessions = () => {
  const [groupSessions, setGroupSessions] = useState<GroupSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch all group sessions for the current user
  const fetchGroupSessions = async () => {
    if (!user) {
      console.log('🔍 No user available for fetching group sessions');
      setGroupSessions([]);
      return;
    }

    console.log('🔒 SECURITY CHECK - Fetching group sessions for user:', user.id);
    console.log('🔒 User email:', user.email);
    setLoading(true);
    setError(null);

    try {
      // Verify current user identity (skip if using backend auth)
      if (import.meta.env.VITE_USE_BACKEND !== 'true') {
        // Only check with Supabase auth if not using backend
        const { data: authUser } = await backendClient.auth.getUser();
        console.log('🔒 Current authenticated user:', authUser.user?.id);
        
        if (!authUser.user || authUser.user.id !== user.id) {
          console.error('🚨 SECURITY ALERT: User ID mismatch!', { 
            contextUserId: user.id, 
            authUserId: authUser.user?.id 
          });
          throw new Error('Authentication mismatch detected');
        }
      } else {
        // For backend auth, trust the user from AuthContext
        console.log('🔒 Using backend auth, user:', user.id);
      }

      const { data, error } = await backendClient
        .from('group_sessions')
        .select('*')
        .eq('owner_id', user.id)
        .eq('is_active', true)
        .order('updated_at', { ascending: false });

      console.log('🔍 Database query result for user', user.id, ':', { 
        totalResults: data?.length || 0,
        error,
        userSpecificQuery: true 
      });

      if (error) throw error;

      // Additional security validation - ensure all returned groups belong to current user
      const invalidGroups = (data || []).filter(group => group.owner_id !== user.id);
      if (invalidGroups.length > 0) {
        console.error('🚨 SECURITY VIOLATION: Found groups not owned by user!', invalidGroups);
        // Log security event
        await backendClient.functions.invoke('log-security-event', {
          body: {
            event_type: 'unauthorized_group_access',
            user_id: user.id,
            event_details: {
              invalid_groups: invalidGroups.map(g => ({ id: g.id, owner_id: g.owner_id })),
              expected_owner: user.id
            }
          }
        });
        throw new Error('Security violation detected');
      }

      // Cast the data to GroupSession[] with proper type conversion
      // IMPORTANT: Clean up legacy participant data that may have extra fields
      const groupSessions = (data || []).map(item => {
        let participants: Participant[] = [];

        if (Array.isArray(item.participants)) {
          // Clean up participant data to only include backend-compatible fields
          // This handles legacy data that was saved before validation was added
          participants = prepareParticipantsForBackend(item.participants as Participant[]);

          if (participants.length !== item.participants.length ||
              JSON.stringify(participants) !== JSON.stringify(item.participants)) {
            console.warn(
              `⚠️ Cleaned legacy participant data for group "${item.group_name}". ` +
              `Removed extra fields from ${item.participants.length} participants.`
            );
          }
        }

        return {
          ...item,
          participants
        };
      }) as GroupSession[];

      console.log('✅ Verified group sessions for user', user.id, ':', groupSessions.length, 'groups');
      setGroupSessions(groupSessions);
      
      if (groupSessions.length === 0) {
        console.log('ℹ️ No group sessions found for user');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch group sessions';
      console.error('❌ Error fetching group sessions:', err);
      setError(errorMessage);
      setGroupSessions([]); // Clear any potentially invalid data
      toast({
        title: 'Error Fetching Groups',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Validate group session data before creation
  const validateGroupSession = (request: CreateGroupSessionRequest): string | null => {
    if (!request.group_name?.trim()) {
      return 'Group name is required';
    }
    if (request.group_name.length > 100) {
      return 'Group name must be less than 100 characters';
    }
    if (!['family', 'organization', 'friends', 'team', 'yoga'].includes(request.group_type)) {
      return 'Invalid group type';
    }
    
    // Validate each participant if participants exist
    if (request.participants && request.participants.length > 0) {
      for (const participant of request.participants) {
        if (!participant.firstName?.trim()) {
          return 'All participants must have a first name';
        }
        if (typeof participant.consentToName !== 'boolean') {
          return 'All participants must have consent preference set';
        }
      }
    }
    
    return null;
  };

  // Create a new group session
  const createGroupSession = async (request: CreateGroupSessionRequest): Promise<GroupSession | null> => {
    if (!user) {
      console.log('❌ No user available for creating group session');
      return null;
    }

    console.log('🔨 Creating group session:', request);

    // Validate input data
    const validationError = validateGroupSession(request);
    if (validationError) {
      console.log('❌ Validation failed:', validationError);
      toast({
        title: 'Validation Error',
        description: validationError,
        variant: 'destructive',
      });
      return null;
    }

    // Check user role and group limit before attempting creation
    if (import.meta.env.VITE_USE_BACKEND !== 'true') {
      const { data: authUser } = await backendClient.auth.getUser();
      if (!authUser.user) {
        toast({
          title: 'Authentication Error',
          description: 'Please log in to create a group',
          variant: 'destructive',
        });
        return null;
      }
    } else {
      // For backend auth, user should already be authenticated
      if (!user) {
        toast({
          title: 'Authentication Error',
          description: 'Please log in to create a group',
          variant: 'destructive',
        });
        return null;
      }
    }

    try {
      // Get user role and subscription info
      const { data: userRole, error: roleError } = await backendClient.rpc('get_user_role', { _user_id: user.id });
      const isAdmin = !roleError && userRole?.[0]?.role === 'admin';

      // If not admin, check group limit based on subscription
      if (!isAdmin) {
        // Get user's subscription tier to check max_groups
        let maxGroups = 1; // Default for individuals

        // Check if user is a practitioner
        const { data: practitionerSub } = await backendClient
          .from('practitioner_subscriptions')
          .select('subscription_tiers!inner(features)')
          .eq('practitioner_id', user.id)
          .single();

        if (practitionerSub) {
          // Practitioner - get max_groups from their subscription
          maxGroups = practitionerSub.subscription_tiers?.features?.max_groups || 1;
        } else {
          // Individual user - get max_groups from their profile's subscription tier
          const { data: profile } = await backendClient
            .from('profiles')
            .select('subscription_tiers!inner(features)')
            .eq('id', user.id)
            .single();

          if (profile?.subscription_tiers?.features?.max_groups) {
            maxGroups = profile.subscription_tiers.features.max_groups;
          }
        }

        // Check if user has reached their limit
        const existingGroups = groupSessions.filter(group => group.is_active);
        if (existingGroups.length >= maxGroups) {
          toast({
            title: 'Group Limit Reached',
            description: `You can only create ${maxGroups} ${maxGroups === 1 ? 'group' : 'groups'}. Please deactivate an existing group or upgrade your plan.`,
            variant: 'destructive',
          });
          return null;
        }
      }
    } catch (roleError) {
      console.log('⚠️ Could not check user role or subscription, proceeding with creation (database will enforce limit)');
    }

    setLoading(true);
    setError(null);

    try {
      // Check if user is a practitioner
      const { data: userRole } = await backendClient.rpc('get_user_role', { _user_id: user.id });
      const isPractitioner = userRole?.[0]?.role === 'practitioner';

      console.log(`🔍 Group creation - User role check: ${isPractitioner ? 'PRACTITIONER' : 'INDIVIDUAL'}`);

      // Fetch user profile to get their name
      const { data: profile } = await backendClient
        .from('profiles')
        .select('first_name, preferred_name, full_name')
        .eq('id', user.id)
        .single();

      const ownerName = profile?.preferred_name || profile?.first_name || profile?.full_name?.split(' ')[0] || 'Me';
      const participants = request.participants || [];

      // Check if owner is already in participants by checking if any participant has matching name
      const ownerAlreadyIncluded = participants.some(p =>
        p.firstName.toLowerCase() === ownerName.toLowerCase() ||
        p.relationshipRole?.toLowerCase() === 'me' ||
        p.relationshipRole?.toLowerCase() === 'owner'
      );

      // IMPORTANT: Only auto-add owner for INDIVIDUAL users creating personal groups
      // Practitioners should NOT be auto-added to client groups
      let finalParticipants = participants;

      if (!ownerAlreadyIncluded && !isPractitioner) {
        console.log('✅ Auto-adding owner as participant (individual user)');
        finalParticipants = [
          {
            firstName: ownerName,
            consentToName: true,
            relationshipRole: 'Owner'
          } as Participant,
          ...participants
        ];
      } else if (isPractitioner) {
        console.log('⚠️ PRACTITIONER detected - NOT auto-adding to group participants');
        console.log(`📋 Group will have ${participants.length} client participants only`);
      } else {
        console.log('ℹ️ Owner already included in participants');
      }

      // VALIDATE participants before sending to backend (BREAKING CHANGE protection)
      if (finalParticipants.length > 0) {
        const validation = validateParticipantsForBackend(finalParticipants);
        if (!validation.isValid) {
          const errorMessage = `Participant validation failed:\n${validation.errors.join('\n')}`;
          console.error('❌ Frontend validation failed:', errorMessage);
          toast({
            title: 'Invalid Participant Data',
            description: parseBackendValidationError(validation.errors[0]),
            variant: 'destructive',
          });
          throw new Error(errorMessage);
        }
        // Use validated participants with only backend-compatible fields
        finalParticipants = validation.fixedParticipants || finalParticipants;
        console.log('✅ Participants validated for backend submission:', finalParticipants);
      }

      const insertData = {
        owner_id: user.id,
        group_name: request.group_name.trim(),
        group_type: request.group_type,
        description: request.description?.trim() || null,
        participants: finalParticipants as any, // Cast to Json for database
        yoga_directives: request.yoga_directives?.trim() || null,
        opening_positions: request.opening_positions?.trim() || null,
        ending_positions: request.ending_positions?.trim() || null,
        session_timing: request.session_timing?.trim() || null,
        organization_name: request.organization_name?.trim() || null,
        organization_biography: request.organization_biography?.trim() || null,
        department: request.department?.trim() || null,
      };

      console.log('🔨 Inserting group data:', insertData);
      console.log(`📊 Participants count: ${finalParticipants.length}${isPractitioner ? ' (practitioner NOT included)' : ' (owner included)'}`);

      const { data, error} = await backendClient
        .from('group_sessions')
        .insert(insertData)
        .select()
        .single();

      console.log('🔨 Insert result:', { data, error });

      if (error) throw error;

      if (!data) {
        throw new Error('No data returned from group session creation');
      }

      // Cast the response data to GroupSession with proper type conversion
      const newGroupSession = {
        ...data,
        participants: Array.isArray(data.participants) ? data.participants as unknown as Participant[] : []
      } as GroupSession;

      console.log('✅ Created group session:', newGroupSession);
      setGroupSessions(prev => [newGroupSession, ...prev]);

      const participantCount = request.participants?.length || 0;
      toast({
        title: 'Group Created Successfully!',
        description: participantCount > 0
          ? `"${request.group_name}" with ${participantCount} members`
          : `"${request.group_name}" created`,
      });

      return newGroupSession;
    } catch (err) {
      let errorMessage = err instanceof Error ? err.message : 'Failed to create group session';
      
      // Handle specific database constraint errors
      if (errorMessage.includes('Group creation limit exceeded') || 
          errorMessage.includes('can only create one group')) {
        errorMessage = 'You can only create one group. Please deactivate your existing group before creating a new one.';
        toast({
          title: 'Group Limit Reached',
          description: errorMessage,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Failed to Create Group',
          description: `${errorMessage}. Please try again or check your connection.`,
          variant: 'destructive',
        });
      }
      
      console.error('❌ Error creating group session:', err);
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing group session
  const updateGroupSession = async (id: string, request: UpdateGroupSessionRequest): Promise<GroupSession | null> => {
    if (!user) return null;

    setLoading(true);
    setError(null);

    try {
      // VALIDATE participants before updating (BREAKING CHANGE protection)
      let validatedParticipants = request.participants;
      if (request.participants && request.participants.length > 0) {
        const validation = validateParticipantsForBackend(request.participants);
        if (!validation.isValid) {
          const errorMessage = `Participant validation failed:\n${validation.errors.join('\n')}`;
          console.error('❌ Frontend validation failed during update:', errorMessage);
          toast({
            title: 'Invalid Participant Data',
            description: parseBackendValidationError(validation.errors[0]),
            variant: 'destructive',
          });
          throw new Error(errorMessage);
        }
        validatedParticipants = validation.fixedParticipants;
        console.log('✅ Participants validated for backend update:', validatedParticipants);
      }

      // Cast the request to match database schema
      const updateData = {
        ...request,
        participants: validatedParticipants ? validatedParticipants as any : undefined,
        yoga_directives: request.yoga_directives?.trim() || null,
        opening_positions: request.opening_positions?.trim() || null,
        ending_positions: request.ending_positions?.trim() || null,
        session_timing: request.session_timing?.trim() || null,
        organization_name: request.organization_name?.trim() || null,
        organization_biography: request.organization_biography?.trim() || null,
        department: request.department?.trim() || null,
      };

      const { data, error } = await backendClient
        .from('group_sessions')
        .update(updateData)
        .eq('id', id)
        .eq('owner_id', user.id)
        .select()
        .single();

      if (error) throw error;

      // Cast the response data to GroupSession with proper type conversion
      const updatedGroupSession = {
        ...data,
        participants: Array.isArray(data.participants) ? data.participants as unknown as Participant[] : []
      } as GroupSession;
      setGroupSessions(prev => 
        prev.map(gs => gs.id === id ? updatedGroupSession : gs)
      );

      toast({
        title: 'Success',
        description: 'Group session updated successfully',
      });

      return updatedGroupSession;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update group session';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Delete a group session (soft delete by setting is_active to false)
  const deleteGroupSession = async (id: string): Promise<boolean> => {
    if (!user) return false;

    setLoading(true);
    setError(null);

    try {
      const { error } = await backendClient
        .from('group_sessions')
        .update({ is_active: false })
        .eq('id', id)
        .eq('owner_id', user.id);

      if (error) throw error;

      setGroupSessions(prev => prev.filter(gs => gs.id !== id));

      toast({
        title: 'Success',
        description: 'Group session deleted successfully',
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete group session';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get a specific group session by ID
  const getGroupSession = (id: string): GroupSession | undefined => {
    return groupSessions.find(gs => gs.id === id);
  };

  useEffect(() => {
    if (user) {
      fetchGroupSessions();
    }
  }, [user]);

  // Manual refresh function
  const refreshGroupSessions = async () => {
    console.log('🔄 Manual refresh triggered');
    await fetchGroupSessions();
  };

  /**
   * Check if user can create another group (based on subscription and current groups)
   * Note: This is a simple UI helper that assumes 1 group limit for the check.
   * The actual limit enforcement based on subscription happens in createGroupSession.
   */
  const canCreateGroup = () => {
    if (!user || loading) return false;

    // Check if user has any active groups
    const activeGroups = groupSessions.filter(group => group.is_active);

    // Simple check: if they have at least one active group, they might be at limit
    // The actual limit (1 for individuals, 1/4/10 for practitioners) is enforced in createGroupSession
    // For better UX, components should use the subscription hook to check exact limits
    return activeGroups.length === 0;
  };

  /**
   * Get information about group creation status
   */
  const getGroupCreationInfo = () => {
    const activeGroups = groupSessions.filter(group => group.is_active);
    const hasActiveGroup = activeGroups.length > 0;

    return {
      hasActiveGroup,
      activeGroupCount: activeGroups.length,
      canCreateGroup: canCreateGroup(),
      existingGroup: hasActiveGroup ? activeGroups[0] : null
    };
  };

  /**
   * Save group's questionnaire preferences for future sessions.
   * Preferences are stored per category to prevent data pollution.
   */
  const saveGroupQuestionnairePreferences = async (
    groupId: string,
    categoryId: string,
    questionnaireData: Record<string, any>
  ): Promise<boolean> => {
    if (!user) {
      console.error('❌ SAVE GROUP PREFERENCES: No authenticated user');
      return false;
    }

    try {
      console.log('💾 Saving group questionnaire preferences:', { groupId, categoryId, questionnaireData });

      // Extract fields that rarely change (personal details, not session-specific)
      const preferencesToStore = {
        // Group details that rarely change
        groupSize: questionnaireData.groupSize,
        groupFrustration: questionnaireData.groupFrustration,
        deeperReason: questionnaireData.deeperReason,

        // Session preferences that might be reused
        preferredStyle: questionnaireData.preferredStyle,
        environment: questionnaireData.environment,
        sessionLength: questionnaireData.sessionLength,
        backgroundSounds: questionnaireData.backgroundSounds,

        // Voice preferences
        voiceGender: questionnaireData.voiceGender,
        voiceType: questionnaireData.voiceType,
        specific_voice_id: questionnaireData.specific_voice_id,

        // Last updated timestamp
        lastUpdated: new Date().toISOString(),
      };

      // Fetch current group preferences from group_sessions table
      const { data: currentGroup, error: fetchError } = await backendClient
        .from('group_sessions')
        .select('preferences')
        .eq('id', groupId)
        .eq('owner_id', user.id)
        .single();

      if (fetchError) {
        console.error('❌ SAVE GROUP PREFERENCES: Error fetching group:', fetchError);
        return false;
      }

      const existingPreferences = (currentGroup?.preferences as Record<string, any>) || {};

      // Store preferences per category to prevent overwriting
      const mergedPreferences = {
        ...existingPreferences,
        questionnaire: {
          ...(existingPreferences.questionnaire || {}),
          [categoryId]: preferencesToStore,
        },
      };

      // Update preferences in group_sessions table
      const { error: updateError } = await backendClient
        .from('group_sessions')
        .update({ preferences: mergedPreferences })
        .eq('id', groupId)
        .eq('owner_id', user.id);

      if (updateError) {
        console.error('❌ SAVE GROUP PREFERENCES: Error updating:', updateError);
        return false;
      }

      console.log(`✅ SAVE GROUP PREFERENCES: Successfully saved preferences for ${categoryId}`);

      // Update local state
      await fetchGroupSessions();

      return true;
    } catch (err: any) {
      console.error('❌ SAVE GROUP PREFERENCES: Caught error:', err);
      return false;
    }
  };

  /**
   * Retrieve group's stored questionnaire preferences for pre-filling forms.
   * Returns category-specific preferences to prevent data pollution.
   */
  const getGroupQuestionnairePreferences = (
    groupId: string,
    categoryId: string
  ): Record<string, any> | null => {
    const group = groupSessions.find(g => g.id === groupId);
    if (!group || !group.preferences) {
      return null;
    }

    const preferences = group.preferences as Record<string, any>;
    const questionnairePrefs = preferences.questionnaire;

    if (!questionnairePrefs || typeof questionnairePrefs !== 'object') {
      return null;
    }

    // Return category-specific preferences
    const categoryPrefs = questionnairePrefs[categoryId];

    if (!categoryPrefs || typeof categoryPrefs !== 'object') {
      console.log(`📋 No group preferences found for category: ${categoryId}`);
      return null;
    }

    console.log(`📋 Retrieved group preferences for ${categoryId}:`, categoryPrefs);
    return categoryPrefs;
  };

  return {
    groupSessions,
    loading,
    error,
    fetchGroupSessions,
    refreshGroupSessions,
    createGroupSession,
    updateGroupSession,
    deleteGroupSession,
    getGroupSession,
    canCreateGroup,
    getGroupCreationInfo,
    saveGroupQuestionnairePreferences,
    getGroupQuestionnairePreferences,
  };
};
